import './gift-detail-modal.scss';

import { Gem } from 'lucide-react';
import { useState } from 'react';

import type { GiftMeta } from '@/pages/shop/api';
import CheckCircleIcon from '@/shared/assets/icons/check-circle.svg?react';
import CopyIcon from '@/shared/assets/icons/copy.svg?react';
import ShareIcon from '@/shared/assets/icons/share.svg?react';
import { env } from '@/shared/config/env';
import { twa } from '@/shared/lib/telegram/twa';
import { BottomSheet } from '@/shared/ui/BottomSheet';

export type GiftDetailItem = GiftMeta & {
  id: string;
  name: string;
  emoji: string;
  slug: string;
  thumbnail_url: string | null;
  value_ton?: number;
  price_ton?: number;
};

interface GiftDetailModalProps {
  open: boolean;
  onClose: () => void;
  item: GiftDetailItem | null;
}

function rarityLabel(permille: number): string {
  if (permille <= 10) return 'Legendary';
  if (permille <= 50) return 'Epic';
  if (permille <= 150) return 'Rare';
  if (permille <= 400) return 'Uncommon';
  return 'Common';
}

export function GiftDetailModal({ open, onClose, item }: GiftDetailModalProps) {
  const [copied, setCopied] = useState(false);

  if (!item) return null;

  const shareText = `${item.name}${item.model_name ? ` — ${item.model_name}` : ''}`;
  const shareUrl = env.TELEGRAM_MINIAPP_LINK ?? 'https://t.me';
  const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;

  const handleCopy = () => {
    const text = [
      item.name,
      item.model_name && `Model: ${item.model_name}`,
      item.pattern_name && `Pattern: ${item.pattern_name}`,
      item.backdrop_name && `Backdrop: ${item.backdrop_name}`,
      item.rarity_permille != null &&
        `Rarity: ${rarityLabel(item.rarity_permille)} (${(item.rarity_permille / 10).toFixed(1)}%)`,
      shareUrl,
    ]
      .filter(Boolean)
      .join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      twa.hapticFeedback.impactOccurred('light');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = () => {
    twa.hapticFeedback.impactOccurred('light');
    twa.openTelegramLink(telegramShareUrl);
  };

  const displayPrice = item.price_ton ?? item.value_ton;
  const attrs: { label: string; value: string }[] = [
    item.model_name ? { label: 'Model', value: item.model_name } : null,
    item.pattern_name ? { label: 'Pattern', value: item.pattern_name } : null,
    item.backdrop_name ? { label: 'Backdrop', value: item.backdrop_name } : null,
    item.is_unique ? { label: 'Type', value: 'Unique' } : null,
    item.collection_num != null
      ? { label: 'Edition', value: `#${item.collection_num}` }
      : null,
    item.rarity_permille != null
      ? {
          label: 'Rarity',
          value: `${rarityLabel(item.rarity_permille)} · ${(item.rarity_permille / 10).toFixed(1)}%`,
        }
      : null,
  ].filter((x): x is { label: string; value: string } => x !== null);

  return (
    <BottomSheet open={open} onClose={onClose} title={item.name}>
      <div className="gift-detail">
        <div className="gift-detail__media gift-detail__media--in">
          {item.thumbnail_url ? (
            <img
              src={item.thumbnail_url}
              alt={item.name}
              className="gift-detail__image"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
                (e.currentTarget.nextSibling as HTMLElement | null)?.removeAttribute('hidden');
              }}
            />
          ) : null}
          <span className="gift-detail__emoji" hidden={!!item.thumbnail_url || undefined}>
            {item.emoji}
          </span>
        </div>

        {displayPrice != null && (
          <div className="gift-detail__price">
            <Gem className="gift-detail__price-icon" />
            <span className="gift-detail__price-value">{displayPrice.toFixed(2)}</span>
            <span className="gift-detail__price-unit">TON</span>
          </div>
        )}

        {attrs.length > 0 && (
          <div className="gift-detail__attrs gift-detail__attrs--in">
            <p className="gift-detail__attrs-title">Attributes</p>
            <div className="gift-detail__attrs-grid">
              {attrs.map((attr) => (
                <div key={attr.label} className="gift-detail__attr">
                  <span className="gift-detail__attr-label">{attr.label}</span>
                  <span className="gift-detail__attr-value">{attr.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="gift-detail__actions gift-detail__actions--in">
          <button
            type="button"
            className="gift-detail__action-btn gift-detail__action-btn--copy gift-detail__action-btn--tappable"
            onClick={handleCopy}>
            {copied ? (
              <CheckCircleIcon className="gift-detail__action-icon" />
            ) : (
              <CopyIcon className="gift-detail__action-icon" />
            )}
            {copied ? 'Copied!' : 'Copy'}
          </button>

          <button
            type="button"
            className="gift-detail__action-btn gift-detail__action-btn--share gift-detail__action-btn--tappable"
            onClick={handleShare}>
            <ShareIcon className="gift-detail__action-icon" />
            Share
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
